/**
 * Display helpers shared by the grids that render "row snapshot + changed
 * fields" payloads (deferred-bonuses audit log, user message rows).
 *
 * Both payloads have the same shape: a `Record<string, string | null>` snapshot
 * of the row plus a list of `{ columnName, oldValue, newValue }` entries for the
 * columns that changed. Keeping the formatting here means both grids show the
 * same `old → new` text and the same highlight for changed cells.
 */

import type { CSSProperties } from 'react'

/** Rendered in place of an empty value so that "value removed" stays visible. */
export const NULL_TEXT = '(null)'

export const ARROW = '→'

/** Inline style applied to a cell whose column appears in the changed-field list. */
export const CHANGED_CELL_STYLE: CSSProperties = { color: '#cf1322', fontWeight: 500 }

export interface FieldChange {
  columnName: string
  oldValue: string | null
  newValue: string | null
}

/** `old → new`, with nulls made visible on either side. */
export const formatFieldChange = (change: Pick<FieldChange, 'oldValue' | 'newValue'>): string =>
  `${change.oldValue ?? NULL_TEXT} ${ARROW} ${change.newValue ?? NULL_TEXT}`

/** Empty string keeps ag-grid filtering/sorting/export consistent for missing values. */
export const displayValue = (value: string | null | undefined): string => value ?? ''
